// src/components/dashboards/add_patient_v2/components/PatientCard.tsx
// -----------------------------------------------------------------------------
// PatientCard — V2 compact redesign (cursor_logs/030 § 2026-05-23).
//
// Apple-style compact layout. The whole card body is the drop target for files
// (no dedicated dropzone block). The card header carries the ID counter on the
// left + an inline editable name field + the Create + remove actions, all in
// one row. Identification + edit happen in the same surface, killing the
// separate "Patient Name *" row that V2-Rev1 had.
//
// Layout (desktop, idle state, no files):
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ #1   [Patient name *                       ]   [Create]    ✕    │  ← header
//   │ ⊕ Add more details          ⬆ Upload files or drop anywhere     │  ← affordance row
//   └──────────────────────────────────────────────────────────────────┘
//
// With files (≤4 inline, ≥5 collapse to summary chip):
//   │ ◉ report.pdf   ◉ bx.pdf                                          │
//   │ ⊕ Add more details          ⬆ Upload files or drop anywhere     │
//
// Drop-target behavior: the whole card body lights up on dragover (bg-tint +
// dashed primary border) — no center overlay. Click-to-browse only fires when
// the user clicks the explicit "Upload files or drop anywhere" affordance
// (other clicks focus their respective inputs / buttons).
//
// Mobile (375 pt): the Create button leaves the header row and renders as a
// full-width button below the card body, to remain a 44 pt tap target. The
// header stays as `#1   [name]   ✕`.
//
// Per-card lifecycle (idle → submitting → polling → succeeded | failed)
// renders as an Alert banner under the header. Succeeded cards freeze the
// name field into a clickable link to /patients/{id}/details; failed cards
// keep the field editable and swap the Create button for a Retry button.
// -----------------------------------------------------------------------------

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  InsertDriveFile as FileIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "../../../../i18n";
import { useExternalIdMatch, ExternalIdOption } from "../hooks/useExternalIdMatch";
import {
  usePatientNameSuggestions,
  PatientNameSuggestion,
} from "../hooks/usePatientNameSuggestions";
import { PatientCardData } from "../types";
import AddMoreDetailsPanel from "./AddMoreDetailsPanel";

// =============================================================================
// Props
// =============================================================================

export interface PatientCardProps {
  index: number;
  card: PatientCardData;
  /** Parent passes stable refs so PatientCard can be wrapped in React.memo
   *  — otherwise typing in one card would re-render every other card. */
  onUpdate: (id: string, patch: Partial<PatientCardData>) => void;
  onRemove: (id: string) => void;
  /** Per-card streaming submit. Fires the unified bulk-upload endpoint with
   *  a single-entry batch + poll-to-completion. Disabled while the card is
   *  mid-flight or already succeeded. */
  onSubmit?: (card: PatientCardData) => void;
  disabled?: boolean;
  /** True when this is the only card and removing it should still be allowed
   *  (it'll be replaced with a fresh empty card by the parent). */
  removable: boolean;
  /** True when a file drag is currently hovering THIS card. Drag-drop is
   *  routed centrally by AddPatientFormV2 (hit-testing), so this is purely
   *  the highlight signal — the card no longer owns drop handlers. */
  isDropActive?: boolean;
  /** Selected environment ID for the form. Used by AddMoreDetailsPanel to
   *  probe the backend for duplicate external_id values in the same env. */
  environmentId?: string;
  /** All ticked environments (cursor_logs/046): the duplicate probe and the
   *  suggestion dropdown cover the batch's whole target world. */
  environmentIds?: string[];
  /** Promote `envId` to the form's creation environment (selectedEnvIds[0]) so
   *  the cross-environment patient can be attached. The parent also surfaces
   *  the "all patients this session go to <env>" warning. §35. */
  onActivateEnvironment?: (envId: string) => void;
  /** Lowercased external_id tokens that appear on MORE THAN ONE card in the
   *  form (computed by the parent across all idle cards). If any of this card's
   *  tokens is in here, two cards would create/attach to the same patient — we
   *  flag it and block this card's submit. See cursor_logs/036 §30. */
  duplicateExternalIds?: string[];
}

// =============================================================================
// Constants / utilities
// =============================================================================

const TABULAR_EXTENSIONS = new Set([".csv", ".xlsx", ".xls"]);
/** Number of file chips beyond which we collapse to a single summary chip.
 *  Joan, 2026-05-23: 4 inline → 5+ collapse. */
const FILE_CHIPS_COLLAPSE_THRESHOLD = 5;

function isTabularFile(name: string): boolean {
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  return TABULAR_EXTENSIONS.has(name.slice(dot).toLowerCase());
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// =============================================================================
// Component
// =============================================================================

const PatientCard: React.FC<PatientCardProps> = ({
  index,
  card,
  onUpdate: onUpdateProp,
  onRemove: onRemoveProp,
  onSubmit: onSubmitProp,
  disabled,
  removable,
  isDropActive = false,
  environmentId,
  environmentIds,
  onActivateEnvironment,
  duplicateExternalIds,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Open by default where there is room for it, collapsed where there is not.
  // On a laptop the extra fields are the point of the form and hiding them
  // behind a chip costs a click on every single patient; on a phone the same
  // fields push the primary name/ID row off the screen. Initial value only:
  // once the user toggles it, their choice stands for the session.
  const detailsDefaultOpen = useMediaQuery(theme.breakpoints.up("md"));
  const [detailsOpen, setDetailsOpen] = useState(detailsDefaultOpen);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  // External-ID chip input draft. The committed chips live in `card.externalId`
  // (the comma-joined string the backend tokenises); this holds ONLY the text
  // the user is currently typing before it commits to a chip. Keeping the two
  // separate is what makes the chip input survive the async merge probe — see
  // the External ID Autocomplete below and cursor_logs/036 §20/§21.
  const [externalIdDraft, setExternalIdDraft] = useState("");
  // Controlled open-state for the Patient-Name dropdown. MUST be controlled:
  // suggestions arrive ~250ms after a keystroke (async + debounce), and an
  // uncontrolled single Autocomplete decides the popup is empty BEFORE they
  // land and never re-opens. Keeping it open while focused lets the late
  // options render. cursor_logs/036 §31.
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  // The §16 "Attach to <name>" confirmation modal was retired in §33: the
  // header "UPDATING <name>" indicator + the "Linked to" pill + the Create
  // button's disabled-while-checking gate already convey that an attach is
  // about to happen, so the modal duplicated info already on screen.
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag highlight is driven by the parent (centralized hit-testing router).
  const isDragging = isDropActive;

  // Same-tab SPA navigation for "go to patient" so the nav-bar back arrow
  // (navigate(-1)) returns to the dashboard. Cmd/Ctrl/Shift-click or
  // middle-click still opens a new tab via the underlying href.
  // Joan, 2026-05-27.
  const goToPatient = useCallback(
    (e: React.MouseEvent, patientId: string) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(`/patients/${patientId}/details`);
    },
    [navigate],
  );

  // Bind parent callbacks to this card's id once per id change. Local
  // children still see the stable `(patch) => …` shape they expect.
  const onUpdate = useCallback(
    (patch: Partial<PatientCardData>) => onUpdateProp(card.id, patch),
    [card.id, onUpdateProp],
  );
  const onRemove = useCallback(
    () => onRemoveProp(card.id),
    [card.id, onRemoveProp],
  );
  // Compute the externalId + name-mirror patch for a given joined value.
  // Pure (no side effects) so both the live onChange handler and the submit
  // flush below can reuse it. Single-chip rule: mirror name only when there's
  // exactly one id; clear an auto-filled name when there are none.
  const computeExternalIdUpdates = useCallback(
    (value: string): Partial<PatientCardData> => {
      const updates: Partial<PatientCardData> = { externalId: value };
      const tokens = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const single = tokens.length === 1 ? tokens[0] : "";
      if (single && (card.nameAutoFilled || !card.name.trim())) {
        updates.name = single;
        updates.nameAutoFilled = true;
        updates.nameMissing = false;
      } else if (tokens.length === 0 && card.nameAutoFilled) {
        updates.name = "";
        updates.nameAutoFilled = false;
      }
      return updates;
    },
    [card.nameAutoFilled, card.name],
  );

  // Submit. CRITICAL (cursor_logs/036 §24): flush any uncommitted draft in the
  // external-id chip input into the submitted value first. Typing "666" and
  // clicking Create without pressing Enter/space/comma must NOT drop the id —
  // the old single-token field committed on every keystroke, chips don't. We
  // build the merged card and pass it straight to the parent so there's no
  // race with the async onUpdate state settle.
  const onSubmit = useCallback(() => {
    const draft = externalIdDraft.trim();
    if (!draft) {
      onSubmitProp?.(card);
      return;
    }
    const tokens = card.externalId
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!tokens.some((tk) => tk.toLowerCase() === draft.toLowerCase())) {
      tokens.push(draft);
    }
    const updates = computeExternalIdUpdates(tokens.join(", "));
    onUpdate(updates); // reflect the flushed chip + name mirror in the UI
    setExternalIdDraft("");
    onSubmitProp?.({ ...card, ...updates });
  }, [card, externalIdDraft, computeExternalIdUpdates, onUpdate, onSubmitProp]);

  // Lifted duplicate-id probe. Runs whenever externalId changes (regardless
  // of whether the user opened the details panel) so the header field below
  // is always in sync with the "merge mode" rendering. The matched-patient
  // lift goes straight into card state via onUpdate. See cursor_logs/036 §16.
  const matchedPatientHandler = useCallback(
    (m: { id: number; name: string; external_id: string } | null) => {
      const patch: Partial<PatientCardData> = { matchedPatient: m };
      if (m) {
        patch.name = m.name;
        patch.nameMissing = false;
      }
      onUpdate(patch);
    },
    [onUpdate],
  );
  // External_id input. The V2 form header uses a MULTI-TAG chip Autocomplete
  // (one chip per external_id), reusing the proven pattern from
  // ExternalIdEditModal. The single source of truth stays `card.externalId`
  // (the comma-joined string the backend tokenises); the chips are DERIVED
  // from it and the typed text lives in `externalIdDraft`. Because the merge
  // probe and the matched-patient mirror never write `card.externalId`, the
  // chips survive the async probe resolution — that was the root cause of the
  // earlier chip-vanish (cursor_logs/036 §20, fixed in §21).
  //
  // The merge probe keys off the EFFECTIVE id — the committed chips PLUS the
  // text currently being typed (`externalIdDraft`) — so a collision is checked
  // (and the Create button shows its "Checking…" spinner) while the user types,
  // not only after they commit a chip. The suggestion list keys off the draft
  // alone (via listQuery). cursor_logs/036 §29.
  const effectiveExternalId = useMemo(() => {
    const committed = card.externalId.trim();
    const draft = externalIdDraft.trim();
    if (!draft) return committed;
    return committed ? `${committed}, ${draft}` : draft;
  }, [card.externalId, externalIdDraft]);
  const {
    checking: externalIdChecking,
    existingIds: externalIdOptions,
    matches: externalIdMatches,
    resolvedFor: externalIdResolvedFor,
  } = useExternalIdMatch({
    externalId: effectiveExternalId,
    environmentId,
    environmentIds,
    enabled: card.status === "idle" || card.status === "failed",
    listQuery: externalIdDraft,
    onMatchedPatientChange: matchedPatientHandler,
  });

  // Cross-patient conflict detection (behaviour D). The probe returns one
  // match per token; the "primary" patient is the one this card is attaching
  // to (the first match, which is also what `matchedPatient` is lifted from).
  // Any token whose match points at a DIFFERENT patient is a conflict: its
  // chip renders red and submit is blocked. Validation is re-checked
  // server-side on submit (cursor_logs/036 §22) — this is the fast UX guard.
  const externalIdConflicts = useMemo(() => {
    const map = new Map<string, { id: number; name: string }>();
    const primaryId = card.matchedPatient?.id ?? externalIdMatches[0]?.id ?? null;
    if (primaryId == null) return map;
    for (const m of externalIdMatches) {
      if (m.id !== primaryId) {
        map.set(m.token.toLowerCase(), { id: m.id, name: m.name });
      }
    }
    return map;
  }, [externalIdMatches, card.matchedPatient]);
  const hasExternalIdConflict = externalIdConflicts.size > 0;

  // Patient-name dropdown (cursor_logs/036 §28, behaviour B). Debounced search
  // of existing patients by name so the user can SELECT a known patient (which
  // populates the external_id → fires the normal probe → "Updating" mode) or
  // FREE-TYPE a brand-new name (no lock — names are not unique). Reuses the
  // env-scoped, anonymisation-aware `GET /patients/?search=` list endpoint.
  const { suggestions: nameSuggestions, loading: nameLoading } =
    usePatientNameSuggestions({
      query: card.name,
      enabled: card.status === "idle" || card.status === "failed",
    });

  // Mirror the external_id back into name when the user hasn't manually
  // touched name. Tracks via `nameAutoFilled` so subsequent external_id
  // keystrokes don't clobber a user-typed name. See cursor_logs/036 §16.
  //
  // Multi-chip rule: the mirror only fires while there is exactly ONE id —
  // mirroring a joined "666, 7777" string into the patient-name field reads
  // wrong. With two or more chips we leave `name` untouched; with zero chips
  // we clear an auto-filled name so the field doesn't keep a stale id.
  const handleExternalIdChange = useCallback(
    (value: string) => {
      onUpdate(computeExternalIdUpdates(value));
    },
    [computeExternalIdUpdates, onUpdate],
  );

  // Set of TICKED env ids (all selected envs, not just the primary), keyed as
  // strings so a numeric /environments/ id and a string suggestion id compare
  // equal (cursor_logs/044). Falls back to the primary when the multi-env list
  // isn't supplied.
  const tickedEnvKeys = useMemo(() => {
    const ids =
      environmentIds && environmentIds.length
        ? environmentIds
        : environmentId
          ? [environmentId]
          : [];
    return new Set(ids.map((id) => String(id)));
  }, [environmentIds, environmentId]);

  // Cross-environment lock decision, SHARED by the name picker and the
  // external-id picker (cursor_logs/047). A bulk job creates/merges only into
  // the TICKED envs; if the picked patient is in NONE of them (it lives only in
  // a visible-but-unticked env), attaching here would spawn a parallel patient,
  // so we lock the card and offer to Activate one of the patient's envs first.
  // If the patient IS in any ticked env, the normal same-env merge handles it →
  // no lock. (Previously the NAME picker checked only the primary env, so a
  // patient in a ticked-but-non-primary env was wrongly locked under multi-env;
  // this also fixes that.)
  const computeCrossEnvLock = useCallback(
    (
      patientEnvIds: Array<string | number>,
      patientEnvNames: Array<string>,
    ): { envId: string; envName: string } | null => {
      if (!patientEnvIds || patientEnvIds.length === 0) return null;
      const inTicked = patientEnvIds.some((e) => tickedEnvKeys.has(String(e)));
      if (inTicked) return null;
      return {
        envId: String(patientEnvIds[0]),
        envName: patientEnvNames?.[0] || String(patientEnvIds[0]),
      };
    },
    [tickedEnvKeys],
  );

  // Name-dropdown SELECT (behaviour B). Picking an existing patient locks the
  // card into update/attach mode: we set the name AND populate external_id with
  // the picked patient's id (which fires the normal merge probe → matchedPatient
  // → "Updating <name>" pill). `nameAutoFilled:false` keeps the chosen name
  // stable against the external_id name-mirror. Free typing (no selection) is
  // handled by the Autocomplete's onInputChange below and never locks.
  const handleNameSuggestionSelect = useCallback(
    (picked: PatientNameSuggestion) => {
      const idValue = picked.external_id.trim();
      const patch: Partial<PatientCardData> = {
        name: picked.name,
        nameMissing: false,
        nameAutoFilled: false,
      };
      if (idValue) {
        patch.externalId = idValue;
      }
      patch.crossEnvLock = computeCrossEnvLock(
        picked.environmentIds,
        picked.environmentNames,
      );
      onUpdate(patch);
    },
    [onUpdate, computeCrossEnvLock],
  );

  // Chips are derived from the single source of truth (`card.externalId`), so
  // they recompute automatically when the joined value changes and stay put
  // across the async merge-probe re-render (which never touches externalId).
  const externalIdTokens = useMemo(
    () =>
      card.externalId
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [card.externalId],
  );

  // Same external_id on TWO cards (behaviour from §30). The parent computes the
  // set of tokens that appear on more than one idle card; if any of this card's
  // tokens is in it, submitting both would create/attach to the same patient
  // twice. We flag those chips and block this card's submit.
  const cardDuplicateTokens = useMemo(() => {
    if (!duplicateExternalIds || duplicateExternalIds.length === 0)
      return new Set<string>();
    const dups = new Set(duplicateExternalIds.map((t) => t.toLowerCase()));
    return new Set(
      externalIdTokens
        .map((t) => t.toLowerCase())
        .filter((t) => dups.has(t)),
    );
  }, [duplicateExternalIds, externalIdTokens]);
  const hasDuplicateExternalId = cardDuplicateTokens.size > 0;

  // Commit a new set of chips: dedup case-insensitively, join back to the
  // comma-separated wire format, and clear the draft. Shared by the comma
  // auto-commit path and MUI's onChange (Enter / option pick / Backspace).
  const commitExternalIdTokens = useCallback(
    (tokens: Array<string | { external_id: string }>) => {
      const seen = new Set<string>();
      const cleaned: string[] = [];
      for (const tok of tokens) {
        const raw = (typeof tok === "string" ? tok : tok.external_id).trim();
        if (!raw || seen.has(raw.toLowerCase())) continue;
        seen.add(raw.toLowerCase());
        cleaned.push(raw);
      }
      handleExternalIdChange(cleaned.join(", "));
      setExternalIdDraft("");
    },
    [handleExternalIdChange],
  );

  // ── Submit-after-probe (cursor_logs/036 §28; replaces §27 timer) ──────────
  // The collision probe only runs on the COMMITTED external_id. If the user
  // types "666" and clicks Create (or unfocuses) WITHOUT pressing
  // Enter/space/comma, the probe never ran, so a collision with an existing
  // patient was never surfaced (the backend would then silently merge, telling
  // the user nothing). §27 closed this with a timer/`probeSeenRef` that raced
  // the `externalIdChecking` flip. §28 replaces that with a DURABLE,
  // state-driven gate: the hook exposes `resolvedFor` — the exact external_id
  // string the current `matches` correspond to. We block submit until the
  // committed value has actually been probed (resolvedFor === it), with no
  // timer and no race window.
  const skipBlurCommitRef = useRef(false);

  // Durable resolution flag (cursor_logs/036 §28/§29). The external_id is
  // "resolved" (safe to submit) when EITHER we cannot probe (no environment →
  // don't block), OR the EFFECTIVE id (committed chips + the text being typed)
  // is empty, OR it has been probed: no probe in flight AND `resolvedFor`
  // matches the effective id. Because the probe keys off the effective id, this
  // flips live as the user types — driving the Create button spinner below.
  const externalIdResolved =
    !environmentId ||
    effectiveExternalId === "" ||
    (!externalIdChecking && externalIdResolvedFor === effectiveExternalId);
  const externalIdPending = !externalIdResolved;

  // Cross-environment lock (cursor_logs/036 §35). Active while the card holds a
  // crossEnvLock whose env is not yet the form's creation env. Once the user
  // activates it (parent promotes it to selectedEnvIds[0] → `environmentId`),
  // this flips false and the normal same-env attach path takes over.
  const crossEnvLocked =
    !!card.crossEnvLock &&
    String(environmentId) !== String(card.crossEnvLock.envId);

  // Clear the lock once its env has become the creation env (activation done).
  useEffect(() => {
    if (
      card.crossEnvLock &&
      String(environmentId) === String(card.crossEnvLock.envId)
    ) {
      onUpdate({ crossEnvLock: null });
    }
  }, [card.crossEnvLock, environmentId, onUpdate]);

  const handleActivateCrossEnv = useCallback(() => {
    if (card.crossEnvLock && onActivateEnvironment) {
      onActivateEnvironment(card.crossEnvLock.envId);
    }
  }, [card.crossEnvLock, onActivateEnvironment]);

  const resolveCreate = useCallback(() => {
    if (crossEnvLocked) return; // must activate the patient's env first (§35)
    if (hasExternalIdConflict) return; // red chip + banner take over; never submit
    // Single submit path now (cursor_logs/036 §33). The "Attach to" confirmation
    // dialog has been retired: the user already sees the "UPDATING <name>"
    // header indicator + the "Linked to" pill + the disabled-while-checking
    // Create button, so an additional modal duplicates information already on
    // screen. onSubmit flushes any uncommitted draft id into the payload (§24)
    // and the backend dedups (merge policy) — so create vs attach is decided
    // by the resolved external_id without an extra click.
    onSubmit();
  }, [crossEnvLocked, hasExternalIdConflict, onSubmit]);

  // The Create button is ONLY enabled when the external_id is fully resolved
  // (the gate below disables it while pending), so a click routes immediately —
  // no timer, no deferred state, no race. By construction you cannot submit an
  // unchecked id.
  const handleCreateClick = resolveCreate;

  // Disable + spinner the Create button while the EFFECTIVE id is still being
  // checked — this covers the user actively typing a draft, not just committed
  // chips. This is the live "Checking…" gate: the button can't be clicked until
  // the backend has confirmed the id.
  // Unified "Create is still being checked" flag covering BOTH the external-id
  // collision probe and the in-flight name search. While either is true the
  // Create button is disabled and shows its spinner — by construction the user
  // cannot submit until the backend has confirmed both lookups. cursor_logs/036
  // §32. (Name suggestions don't gate creation by themselves — names aren't
  // unique — but while the search is in flight a SELECTION could still land
  // that populates the external_id, so we wait for it to settle.)
  // Drives BOTH the disabled state AND the "checking…" spinner on Create, so it
  // must stay limited to genuinely-in-flight lookups. The cross-env lock
  // disables Create separately (no spinner) — see the button `disabled` props.
  const externalIdDisablesCreate = externalIdPending || nameLoading;

  // True when neither a name nor an external ID has been entered. The backend
  // needs at least ONE of the two (name OR external_id), so the Create button
  // stays disabled until one is present. Surfaced in the Create button's
  // tooltip so the user knows WHY it's inactive instead of guessing.
  const missingNameAndId =
    !card.name.trim() && !card.externalId.trim() && !externalIdDraft.trim();

  // Human-readable reason the Create button is currently disabled, shown as its
  // hover tooltip. Ordered by precedence: in-flight check → missing identifier →
  // retry/create labels. (Conflict/cross-env states have their own inline UI.)
  const createButtonTooltip = externalIdDisablesCreate
    ? t("patients_v2.checking_external_id", "Checking external ID…")
    : missingNameAndId
      ? t(
          "patients_v2.create_needs_name_or_id",
          "Enter a patient name or an external ID to create the patient.",
        )
      : card.status === "failed"
        ? t("patients_v2.card_retry", "Retry")
        : t("patients_v2.card_create_one", "Create patient");

  // Lift the pending + conflict flags to card state so the parent's bulk
  // "Create all" button can stay disabled while ANY idle card is still probing
  // OR has a cross-patient conflict. Guard each update so we only write when the
  // value actually changes (PatientCard is React.memo'd; a redundant onUpdate
  // would churn the parent every render).
  // Lift the *unified* gate flag (external-id probe OR name search in flight)
  // so the parent's bulk "Create N" button stays disabled until every idle
  // card's lookups have settled. Mirrors the per-card disable. §32.
  const checksInFlight = externalIdPending || nameLoading;
  useEffect(() => {
    if (card.externalIdPending !== checksInFlight) {
      onUpdate({ externalIdPending: checksInFlight });
    }
  }, [checksInFlight, card.externalIdPending, onUpdate]);
  useEffect(() => {
    if (card.externalIdConflict !== hasExternalIdConflict) {
      onUpdate({ externalIdConflict: hasExternalIdConflict });
    }
  }, [hasExternalIdConflict, card.externalIdConflict, onUpdate]);
  // Lift the cross-env lock so the bulk "Create all" button stays disabled
  // while any idle card needs its environment activated first. §35.
  useEffect(() => {
    if (card.crossEnvBlocked !== crossEnvLocked) {
      onUpdate({ crossEnvBlocked: crossEnvLocked });
    }
  }, [crossEnvLocked, card.crossEnvBlocked, onUpdate]);

  // Commit a leftover draft when the field loses focus (e.g. the user clicks
  // "Add more details" or anywhere else) so the id is never silently dropped
  // and the collision probe always runs. Skipped right after an option pick /
  // chip commit (onChange already handled it and cleared the draft).
  const handleExternalIdBlur = useCallback(() => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    const draft = externalIdDraft.trim();
    if (draft) commitExternalIdTokens([...externalIdTokens, draft]);
  }, [externalIdDraft, externalIdTokens, commitExternalIdTokens]);

  // (The §16 idempotency probe + duplicate-description warning were retired
  // alongside the Attach confirmation modal — see §33.)

  // ── Lifecycle-derived flags ─────────────────────────────────────────────
  const isReadOnly =
    card.status === "submitting" ||
    card.status === "polling" ||
    card.status === "succeeded";
  const isMidFlight =
    card.status === "submitting" || card.status === "polling";
  const inputsDisabled = disabled || isReadOnly;

  // ── Drop handlers ───────────────────────────────────────────────────────
  const acceptFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      if (card.status === "succeeded") {
        enqueueSnackbar(
          t(
            "patients_v2.succeeded_card_drop_blocked",
            "This patient was already created. To add more documents, open the patient's detail page from the link in this card.",
          ),
          { variant: "info", autoHideDuration: 7000 },
        );
        return;
      }
      if (isMidFlight) return;
      const tabularDrops = incoming.filter((f) => isTabularFile(f.name));
      if (tabularDrops.length > 0) {
        enqueueSnackbar(
          t(
            "patients_v2.per_card_excel_warning",
            "You added a spreadsheet for this patient only. It will be treated as a single document. To create multiple patients from a spreadsheet, drop it in the 'Add patients' area below.",
          ),
          { variant: "warning", autoHideDuration: 7000 },
        );
      }
      onUpdate({ files: [...card.files, ...incoming] });
    },
    [card.files, card.status, enqueueSnackbar, isMidFlight, onUpdate, t],
  );

  // Drag-drop onto the card is handled centrally by AddPatientFormV2 (it
  // hit-tests the pointer and routes files into the right card). The card
  // no longer owns onDragOver/onDrop — that was the source of the flaky
  // double-handling and the inner-drop-activates-outer bug.
  const handleBrowseClick = () => {
    if (!inputsDisabled) fileInputRef.current?.click();
  };
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      acceptFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };
  const handleFileRemove = (fileIdx: number) => {
    if (isReadOnly) return;
    onUpdate({ files: card.files.filter((_, i) => i !== fileIdx) });
  };

  const cardLabel = card.name.trim();
  const showInlineCreate = !isMobile;
  const fileCount = card.files.length;
  const hasManyFiles = fileCount >= FILE_CHIPS_COLLAPSE_THRESHOLD;

  // ── Mobile-only Create row content (memoized only for clarity) ──────────
  const mobileCreateButton = useMemo(() => {
    if (!isMobile) return null;
    if (isMidFlight) return null;
    // After success, show a "Go to patient" button instead of Create.
    if (card.status === "succeeded") {
      if (!card.createdPatientId) return null;
      const pid = card.createdPatientId;
      return (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          component="a"
          href={`/patients/${pid}/details`}
          onClick={(e) => goToPatient(e, pid)}
          startIcon={<OpenInNewIcon />}
          sx={{ minHeight: 44, textTransform: "none" }}
        >
          {t("patients_v2.card_go_to_patient", "Go to patient")}
        </Button>
      );
    }
    if (!onSubmit) return null;
    const inMergeMode = Boolean(card.matchedPatient);
    return (
      <Button
        fullWidth
        variant={card.status === "failed" ? "outlined" : "contained"}
        color={
          card.status === "failed"
            ? "error"
            : inMergeMode
              ? "success"
              : "primary"
        }
        startIcon={
          externalIdDisablesCreate ? (
            <CircularProgress size={16} color="inherit" />
          ) : card.status === "failed" ? (
            <RefreshIcon />
          ) : (
            <PersonAddIcon />
          )
        }
        // handleCreateClick commits any uncommitted draft and waits for the
        // collision probe before routing to the "Attach to <name>" confirm
        // (collision) or a normal create (free). See cursor_logs/036 §27.
        onClick={handleCreateClick}
        // Onboarding tutorial anchor on the mobile Create button (first card
        // only) so the "submit-create-patient" step still has a target on
        // small viewports. Mirrors the desktop IconButton anchor.
        data-tour={index === 0 ? "submit-create-patient" : undefined}
        // Patient.name is nullable in the backend, so submit doesn't require
        // a name — only a name OR external_id (the backend falls back to
        // "Patient N" when both are blank). Blocked while any chip conflicts
        // with another patient (behaviour D) or while the probe is in flight.
        disabled={
          disabled ||
          hasExternalIdConflict ||
          hasDuplicateExternalId ||
          crossEnvLocked ||
          externalIdDisablesCreate ||
          (!card.name.trim() &&
            !card.externalId.trim() &&
            !externalIdDraft.trim())
        }
        sx={{ minHeight: 44, textTransform: "none" }}
      >
        {card.status === "failed"
          ? t("patients_v2.card_retry", "Retry")
          : inMergeMode
            ? t(
                "patients_v2.card_attach_to",
                "Attach to {{name}}",
              ).replace("{{name}}", card.matchedPatient?.name || "")
            : t("patients_v2.card_create_one", "Create patient")}
      </Button>
    );
  }, [card.createdPatientId, card.externalId, card.matchedPatient, card.name, card.status, crossEnvLocked, disabled, externalIdDisablesCreate, externalIdDraft, goToPatient, handleCreateClick, hasDuplicateExternalId, hasExternalIdConflict, isMidFlight, isMobile, onSubmit, t]);

  // Border colour mirrors the PatientDetails GlassBox "outlined" treatment:
  // periwinkle 0.55 at rest, ramps to status colours when the lifecycle warrants.
  const outerBorderColor = isDragging
    ? theme.palette.primary.main
    : card.status === "succeeded"
    ? alpha(theme.palette.success.main, 0.55)
    : card.status === "failed"
    ? alpha(theme.palette.error.main, 0.55)
    : isMidFlight
    ? alpha(theme.palette.primary.main, 0.45)
    : alpha(theme.palette.primary.main, 0.5); // idle: visible dashed "drop here" hint

  // Border STYLE: dashed at rest / while dragging to hint "you can drop files
  // onto this patient"; solid only for the final succeeded/failed states and
  // while mid-flight (Joan, 2026-05-29).
  const isIdleDroppable =
    !isDragging &&
    card.status !== "succeeded" &&
    card.status !== "failed" &&
    !isMidFlight;
  const outerBorderStyle = isDragging || isIdleDroppable ? "dashed" : "solid";

  // Header gradient — deep-navy filled GlassBox treatment.
  const headerGradient = `linear-gradient(180deg, ${alpha(
    theme.palette.primary.dark,
    0.95,
  )} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`;

  // Body gradient — outlined GlassBox "lighter" treatment (alpha 0.10 navy on
  // both stops to read as a flat tinted glass beneath the navy header).
  const bodyGradient = `linear-gradient(180deg, ${alpha(
    theme.palette.primary.dark,
    0.10,
  )} 0%, ${alpha(theme.palette.primary.dark, 0.10)} 100%)`;

  return (
    // Outer relative wrapper — hosts the floating red ✕ that sits at the
    // top-right corner of the card (Joan, 2026-05-25). The card itself keeps
    // `overflow: hidden` so the navy gradient is clipped by the border-radius;
    // the ✕ lives OUTSIDE the card's overflow, layered above it via z-index.
    <Box sx={{ position: "relative" }}>
      {/* Floating remove button — error-tinted GRADIENT round button.
          Matches the gradient-round-button grammar that PatientDetails uses
          for `Generate Report` / `Find Trials` and that the per-card Create
          button uses inside the header. Same border, same glassShadow, same
          backdrop blur, same hover translate — but error-coloured so the
          red still reads as "remove this patient." Sits partially outside
          the card's top-right corner via `position: absolute` + negative
          offsets; the parent `position: relative` wrapper guarantees the
          button isn't clipped by the card's `overflow: hidden`.
          Hidden while mid-flight (would orphan the request) and on
          succeeded (the patient is already created; only the backend
          can undo that). */}
      {/* Joan, 2026-05-25: hidden on a single EMPTY card, where removing it
          would leave the user staring at an empty form for no reason.
          Joan, 2026-09-09: but a LONE card that has data IS removable, and
          there ✕ acts as a reset: removeCard swaps the last card for a fresh
          empty one, which is exactly the "clear this and start over" the user
          wants. The parent passes
          `removable = cards.length > 1 || !isCardEmpty(card)`. */}
      {removable && card.status !== "succeeded" && !isMidFlight && (
        <Tooltip title={t("action.remove", "Remove")} arrow placement="left">
          <span style={{
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 2,
          }}>
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={disabled || !removable}
              aria-label={t("action.remove", "Remove")}
              sx={{
                width: 24,
                height: 24,
                p: 0,
                background: `linear-gradient(180deg, ${alpha(
                  theme.palette.error.main,
                  0.95,
                )} 0%, ${alpha(theme.palette.error.main, 0.85)} 100%)`,
                color: theme.palette.common.white,
                border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                boxShadow: theme.custom?.glassShadow,
                backdropFilter: "blur(6px)",
                transition: "all .25s ease",
                "&:hover": {
                  background: `linear-gradient(180deg, ${alpha(
                    theme.palette.error.dark,
                    1,
                  )} 0%, ${alpha(theme.palette.error.dark, 0.9)} 100%)`,
                  transform: "translateY(-1px)",
                  boxShadow: `${theme.custom?.glassShadow}, 0 4px 12px ${alpha(
                    theme.palette.error.dark,
                    0.25,
                  )}`,
                },
                "&.Mui-disabled": {
                  background: alpha(theme.palette.common.white, 0.08),
                  color: alpha(theme.palette.common.white, 0.35),
                  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Box
        data-patient-card-id={card.id}
        sx={{
          border: `${outerBorderStyle === "dashed" ? "1.5px" : "1px"} ${outerBorderStyle} ${outerBorderColor}`,
          borderRadius: theme.shape.borderRadius / 6,
          overflow: "hidden",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: isDragging
            ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.10)}`
            : "none",
          opacity: card.status === "succeeded" ? 0.94 : 1,
          // Joan, 2026-05-27: plain mouse-hover affordance so it's obvious the
          // whole card (header included) is a drop target — independent of any
          // active drag. The drag highlight is a separate DOM outline applied
          // by the active drag-drop strategy.
          "&:hover":
            isReadOnly
              ? undefined
              : {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
        }}
      >
      {/* ── HEADER STRIP — navy gradient. Padding and height now exactly
          mirror the PatientDetails `GlassBox variant="filled"` section
          headers (px:3, py:0.5). Slim row with only: #N, name, Create.
          ✕ moved to floating top-right; ExpandToggle moved to the body
          (Joan, 2026-05-25). ──── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          px: 3,
          // Joan, 2026-05-27: extra top padding so the outlined-input's
          // floating "Patient Name *" label has room to sit ABOVE the
          // input's top border without being clipped by the card's
          // overflow:hidden.
          pt: 1.5,
          pb: 0.5,
          minHeight: 48,
          background: headerGradient,
          color: theme.palette.common.white,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        }}
      >
        {/* ID counter — V1-pill style chip (Joan, 2026-05-27). Same size
            family as the "+ New Environment" chip: 24px tall, 0.72rem font,
            rounded-square. Reads as a quiet sequence marker, not a heading. */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 24,
            minWidth: 30,
            px: 0.9,
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.common.white, 0.12),
            color: alpha(theme.palette.common.white, 0.85),
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: 0.2,
            flexShrink: 0,
          }}
        >
          #{index + 1}
        </Box>

        {/* Resolution-state indicator (cursor_logs/036 §28, behaviour A). A
            small, unobtrusive caption next to the #N counter that tells the
            user whether this card will UPDATE an existing patient (the probe
            matched one → teal "Updating <name>") or CREATE a new one (neutral
            "New patient"). Only shown while the card is idle/failed and the
            user has typed something; succeeded/mid-flight states are covered by
            the lifecycle chips. Complements the "Linked to" merge pill. */}
        {(card.status === "idle" || card.status === "failed") &&
          (card.matchedPatient ? (
            <Typography
              component="span"
              data-test="patient-card-mode-indicator"
              sx={{
                fontSize: "0.66rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: theme.palette.success.light,
                flexShrink: 0,
                whiteSpace: "nowrap",
                maxWidth: { xs: 90, sm: 160 },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t("patients_v2.mode_updating", "Updating {name}").replace(
                "{name}",
                card.matchedPatient.name,
              )}
            </Typography>
          ) : card.name.trim() || card.externalId.trim() ? (
            <Typography
              component="span"
              data-test="patient-card-mode-indicator"
              sx={{
                fontSize: "0.66rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: alpha(theme.palette.info.light, 0.85),
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {t("patients_v2.mode_new_patient", "New patient")}
            </Typography>
          ) : null)}

        {/* Editable name field — white on navy with periwinkle placeholder.
            On succeeded becomes a Link sized identically so the row doesn't
            jitter. */}
        {card.createdPatientId &&
        (card.status === "succeeded" || card.status === "polling") ? (
          // Once the patient row exists in the DB (status: polling | succeeded)
          // expose the deep link so the clinician can hop into the patient
          // page and watch extraction finish from there — instead of being
          // stuck on this card. Warning-tinted while still polling so it's
          // visually clear the data isn't fully extracted yet.
          <Tooltip
            title={
              card.status === "polling"
                ? t(
                    "patients_v2.card_link_polling_tooltip",
                    "Patient created — extraction still in progress. Open to watch it finish.",
                  )
                : t(
                    "patients_v2.card_link_success_tooltip",
                    "Open the patient",
                  )
            }
            arrow
          >
            <Link
              href={`/patients/${card.createdPatientId}/details`}
              onClick={(e) => goToPatient(e, card.createdPatientId as string)}
              underline="hover"
              sx={{
                flexGrow: 1,
                fontSize: "1.05rem",
                fontWeight: 600,
                letterSpacing: 0,
                lineHeight: 1.3,
                color:
                  card.status === "polling"
                    ? theme.palette.warning.light
                    : alpha(theme.palette.common.white, 0.95),
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                "&:hover": {
                  color:
                    card.status === "polling"
                      ? theme.palette.warning.main
                      : theme.palette.common.white,
                },
              }}
            >
              {cardLabel || t("patients_v2.card_no_name", "(no name)")}
              {card.status === "polling" && (
                <CircularProgress
                  size={12}
                  thickness={5}
                  sx={{ color: theme.palette.warning.light, flexShrink: 0 }}
                />
              )}
            </Link>
          </Tooltip>
        ) : card.matchedPatient ? (
          // "Merge mode" — the duplicate-id probe resolved to an existing
          // patient. Replace the name input with a non-editable pill that
          // makes it crystal clear the card will attach to that patient's
          // record rather than create a new one. The user can still erase
          // the external_id to drop back into create-new mode.
          // See cursor_logs/036 §16.
          <Box
            data-test="patient-card-merge-pill"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 1.25,
              backgroundColor: alpha(theme.palette.success.light, 0.18),
              border: `1px solid ${alpha(theme.palette.success.light, 0.55)}`,
              color: theme.palette.common.white,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: alpha(theme.palette.success.light, 0.95),
                flexShrink: 0,
              }}
            >
              {t("patients_v2.linked_to", "Linked to")}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "0.95rem",
                fontWeight: 600,
                color: theme.palette.common.white,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {card.matchedPatient.name}
            </Typography>
          </Box>
        ) : (
          // Patient Name — Joan, 2026-06-05 (cursor_logs/036 §28, behaviour B):
          // a freeSolo single Autocomplete. Free typing behaves exactly like the
          // old TextField (sets card.name, no lock — names are not unique).
          // SELECTING an existing patient from the dropdown locks the card into
          // "Updating <name>" mode (populates external_id → fires the probe).
          // The sx mirrors V1's outlined TextField shape, kept identical to the
          // External ID Autocomplete next to it so both header fields align.
          <Autocomplete<PatientNameSuggestion, false, false, true>
            freeSolo
            size="small"
            // Controlled open (async pattern): stay open ONLY when there are
            // real options to show. We never pass `loading` to the Autocomplete
            // because MUI then renders an in-popup "Loading…" row; the spinner
            // lives next to the input (endAdornment) instead, matching the
            // External-ID field. cursor_logs/036 §31/§32.
            open={nameDropdownOpen && nameSuggestions.length > 0}
            onOpen={() => setNameDropdownOpen(true)}
            onClose={() => setNameDropdownOpen(false)}
            options={nameSuggestions}
            // Controlled as freeSolo: the committed value is always the raw
            // string in card.name; options only drive the dropdown + select.
            value={card.name}
            inputValue={card.name}
            disabled={inputsDisabled}
            filterOptions={(x) => x}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            isOptionEqualToValue={() => false}
            onInputChange={(_, value, reason) => {
              // Free typing (and the post-select reset) updates card.name. A
              // real edit stops the external_id name-mirror. We DON'T touch
              // external_id here — only an explicit option pick locks the card.
              if (reason === "input") {
                // Keep the dropdown open so async suggestions render when they
                // arrive (MUI won't re-open an empty popup on its own).
                setNameDropdownOpen(true);
              }
              if (reason === "input" || reason === "clear") {
                onUpdate({
                  name: value,
                  nameMissing: false,
                  nameAutoFilled: false,
                  // Free-typing abandons a previously-picked cross-env patient,
                  // so drop the lock too (§35). Guarded to avoid churn.
                  ...(card.crossEnvLock ? { crossEnvLock: null } : {}),
                });
              }
            }}
            onChange={(_, picked) => {
              // Only an option object (not a free-typed string) locks the card.
              if (picked && typeof picked !== "string") {
                handleNameSuggestionSelect(picked);
              }
            }}
            renderOption={(props, option) =>
              typeof option === "string" ? null : (
                <Box
                  component="li"
                  {...props}
                  key={`${option.id}-${option.external_id}`}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    {option.external_id && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {option.external_id}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            }
            sx={{
              flexGrow: 1,
              minWidth: 0,
              // Joan, 2026-06-03 (cursor_logs/036 §16): align Name's input
              // font + label sizes to the External ID Autocomplete next to
              // it so the two header fields render at the same height.
              "& .MuiOutlinedInput-root": {
                fontSize: "0.9rem",
                fontWeight: 500,
                color: theme.palette.common.white,
                background: alpha(theme.palette.common.white, 0.05),
                "& fieldset": {
                  borderColor: alpha(theme.palette.common.white, 0.35),
                },
                "&:hover fieldset": {
                  borderColor: alpha(theme.palette.common.white, 0.55),
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.common.white,
                  borderWidth: 1,
                },
                "&.Mui-error fieldset": {
                  borderColor: theme.palette.error.light,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 0.7,
                // The global theme paints input text deep-navy
                // (onLight.primary), which is invisible on this card's navy
                // header once the field is filled and blurred. Force white on
                // the input element itself so it stays high-contrast in every
                // state.
                color: theme.palette.common.white,
                WebkitTextFillColor: theme.palette.common.white,
              },
              "& .MuiInputLabel-root": {
                color: alpha(theme.palette.common.white, 0.7),
                fontWeight: 500,
                fontSize: "0.8rem",
                "&.Mui-focused": { color: theme.palette.common.white },
                "&.Mui-error": { color: theme.palette.error.light },
              },
              // Joan, 2026-05-27: the non-shrink label was sitting too high.
              // Anchor it to vertical center of the (custom-height) input via
              // top:50% + translateY(-50%) so it stays centred regardless of
              // input padding tweaks.
              "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                top: "50%",
                transform: "translate(14px, -50%) scale(1)",
              },
              // Floating (shrunk) label background matches the navy card so
              // it floats cleanly above the border.
              "& .MuiInputLabel-shrink": {
                background: theme.palette.primary.dark,
                px: 0.6,
              },
              "& .MuiInputBase-input.Mui-disabled": {
                color: alpha(theme.palette.common.white, 0.75),
                WebkitTextFillColor: alpha(theme.palette.common.white, 0.75),
              },
              "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator":
                { color: alpha(theme.palette.common.white, 0.7) },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={card.nameMissing}
                // The backend Patient.name is nullable. Drop the required-field
                // asterisk so the form mirrors the storage contract; user can
                // submit a patient with only an external_id. (cursor_logs/036 §16)
                variant="outlined"
                label={t("patients.name_label", "Patient Name")}
                placeholder={t("patients.name_label_short", "Patient name")}
                data-tour={index === 0 ? "patient-name" : undefined}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": t("patients.name_label", "Patient Name"),
                  "data-test": "patient-card-name",
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {nameLoading && (
                        <CircularProgress
                          size={14}
                          sx={{
                            color: alpha(theme.palette.common.white, 0.7),
                            mr: 0.5,
                          }}
                        />
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {/* External ID — Joan, 2026-06-04 (cursor_logs/036 §21): multi-tag
            chip input. One chip per external_id; type + Enter/comma adds a
            chip, × or Backspace removes one. The chips are derived from
            `card.externalId` (the single comma-joined source of truth for the
            merge-mode probe and the bulk-upload payload) and the typed text
            lives in `externalIdDraft`. Suggestions browse existing IDs in the
            env; the info-icon tooltip carries the NHC/MRN guidance. Hidden in
            succeeded state — the patient-name link replaces it. */}
        {!card.createdPatientId && (
          <Autocomplete<
            ExternalIdOption,
            true,
            false,
            true
          >
            multiple
            freeSolo
            size="small"
            value={externalIdTokens}
            inputValue={externalIdDraft}
            onInputChange={(_, value, reason) => {
              // Real typing clears the skip-blur flag so a draft entered AFTER
              // a chip add/remove still commits on blur.
              if (reason === "input") skipBlurCommitRef.current = false;
              // MUI fires reason="reset" with empty value after a chip commits
              // or an option is picked — let that empty the input.
              if (reason === "reset") {
                setExternalIdDraft(value);
                return;
              }
              // Any delimiter auto-commits — comma OR whitespace (space/tab).
              // External IDs (NHC/MRN/affiliation/study IDs) never contain
              // spaces, so a space just means "next id"; this matches the
              // usual tag-input convention and is faster than reaching for the
              // comma. "666 7777" and "666, 7777" both land as two chips.
              if (/[,\s]/.test(value)) {
                const parts = value
                  .split(/[,\s]+/)
                  .map((s) => s.trim())
                  .filter(Boolean);
                if (parts.length > 0) {
                  commitExternalIdTokens([...externalIdTokens, ...parts]);
                } else {
                  setExternalIdDraft("");
                }
                return;
              }
              setExternalIdDraft(value);
            }}
            onChange={(_, newValue) => {
              // newValue is the full (string | option)[] after an add (Enter /
              // option pick) or a remove (× / Backspace). commit dedups +
              // joins back to the wire format and clears the draft. Flag so the
              // following blur (option pick blurs the input) doesn't re-commit
              // a stale partial draft as an extra chip.
              skipBlurCommitRef.current = true;
              // Cross-env on PICK (cursor_logs/047): if the user selected an
              // EXISTING id from the dropdown and that patient lives in NO
              // ticked env, lock the card + offer "Activate <env>" — identical
              // to a cross-env name pick. A pick of a ticked-env id clears any
              // stale lock. Typed (string) commits leave the lock untouched.
              const pickedOption = newValue.find(
                (v): v is ExternalIdOption => typeof v !== "string",
              );
              if (pickedOption && pickedOption.environment_ids) {
                onUpdate({
                  crossEnvLock: computeCrossEnvLock(
                    pickedOption.environment_ids,
                    pickedOption.environment_names || [],
                  ),
                });
              }
              commitExternalIdTokens(newValue);
            }}
            options={externalIdOptions}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.external_id
            }
            filterOptions={(x) => x}
            disabled={inputsDisabled}
            renderTags={(value, getTagProps) =>
              value.map((token, idx) => {
                // freeSolo committed values are strings, but MUI types the
                // value element as (string | option); coerce to the id text.
                const label =
                  typeof token === "string" ? token : token.external_id;
                // Behaviour D: a token belonging to a different patient turns
                // its chip red with a warning icon; the banner in the body
                // explains which patient owns it.
                const conflict = externalIdConflicts.has(label.toLowerCase());
                return (
                  <Chip
                    {...getTagProps({ index: idx })}
                    key={`${label}-${idx}`}
                    label={label}
                    size="small"
                    icon={
                      conflict ? (
                        <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                      ) : undefined
                    }
                    sx={{
                      height: 22,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      bgcolor: conflict
                        ? alpha(theme.palette.error.main, 0.22)
                        : alpha(theme.palette.common.white, 0.16),
                      color: conflict
                        ? theme.palette.error.light
                        : theme.palette.common.white,
                      border: `1px solid ${
                        conflict
                          ? theme.palette.error.light
                          : alpha(theme.palette.common.white, 0.3)
                      }`,
                      "& .MuiChip-icon": { color: theme.palette.error.light },
                      "& .MuiChip-deleteIcon": {
                        color: conflict
                          ? alpha(theme.palette.error.light, 0.8)
                          : alpha(theme.palette.common.white, 0.7),
                        "&:hover": {
                          color: conflict
                            ? theme.palette.error.light
                            : theme.palette.common.white,
                        },
                      },
                    }}
                  />
                );
              })
            }
            renderOption={(props, option) =>
              typeof option === "string" ? null : (
                <Box
                  component="li"
                  {...props}
                  key={`${option.patient_id}-${option.external_id}`}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.external_id}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {option.patient_name}
                    </Typography>
                  </Box>
                </Box>
              )
            }
            sx={{
              flexBasis: { xs: "40%", sm: "32%" },
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.9rem",
                fontWeight: 500,
                color: theme.palette.common.white,
                background: alpha(theme.palette.common.white, 0.05),
                py: 0.25,
                "& fieldset": {
                  borderColor: alpha(theme.palette.common.white, 0.35),
                },
                "&:hover fieldset": {
                  borderColor: alpha(theme.palette.common.white, 0.55),
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.common.white,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 0.7,
                color: theme.palette.common.white,
                WebkitTextFillColor: theme.palette.common.white,
              },
              "& .MuiInputLabel-root": {
                color: alpha(theme.palette.common.white, 0.7),
                fontWeight: 500,
                fontSize: "0.8rem",
                "&.Mui-focused": { color: theme.palette.common.white },
              },
              "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                top: "50%",
                transform: "translate(14px, -50%) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                background: theme.palette.primary.dark,
                px: 0.6,
              },
              "& .MuiInputBase-input.Mui-disabled": {
                color: alpha(theme.palette.common.white, 0.75),
                WebkitTextFillColor: alpha(theme.palette.common.white, 0.75),
              },
              "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator":
                { color: alpha(theme.palette.common.white, 0.7) },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                onBlur={handleExternalIdBlur}
                label={t(
                  "patients_v2.external_id_label",
                  "External ID (NHC / MRN / Affiliation ID)",
                )}
                placeholder={
                  externalIdTokens.length === 0
                    ? t(
                        "patients_v2.external_id_chip_placeholder",
                        "External ID",
                      )
                    : ""
                }
                inputProps={{
                  ...params.inputProps,
                  "aria-label": t(
                    "patients_v2.external_id_label",
                    "External ID (NHC / MRN / Affiliation ID)",
                  ),
                  "data-test": "patient-card-external-id",
                }}
                InputProps={{
                  ...params.InputProps,
                  // Preserve the chips MUI renders into startAdornment; just
                  // prepend the info-icon tooltip ahead of them.
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                        <Tooltip
                          title={t(
                            "patients_v2.external_id_help",
                            "Hospital identifier (NHC, MRN, Affiliation ID, study ID). Add several separated by a space or comma.",
                          )}
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: alpha(theme.palette.common.white, 0.7),
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {externalIdChecking && (
                        <CircularProgress
                          size={14}
                          sx={{
                            color: alpha(theme.palette.common.white, 0.7),
                            mr: 0.5,
                          }}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {/* Source-tabular chip — onDark variant matching AddFieldButton. */}
        {card.source === "tabular" && (
          <Chip
            icon={<TableChartIcon sx={{ fontSize: 13 }} />}
            label={t("patients_v2.from_spreadsheet", "from spreadsheet")}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 500,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.common.white, 0.12),
              color: alpha(theme.palette.common.white, 0.85),
              border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
              "& .MuiChip-icon": { color: alpha(theme.palette.common.white, 0.85) },
            }}
          />
        )}

        {/* Lock chip on succeeded — re-uses EditableTable's lock vocabulary
            so users learn the trust-hierarchy badge before they ever hit
            PatientDetails. */}
        {card.status === "succeeded" && (
          <Tooltip
            title={t(
              card.matchedPatient
                ? "patients_v2.succeeded_card_merged_tooltip"
                : "patients_v2.succeeded_card_drop_blocked",
              card.matchedPatient
                ? "This card was attached to an existing patient. Open the patient's detail page to see the new chat message and the updated tables."
                : "This patient was already created. To add more documents, open the patient's detail page from the link in this card.",
            )}
            arrow
            placement="top"
          >
            <Chip
              icon={<LockIcon sx={{ fontSize: 12 }} />}
              // Label tells the user what actually happened: "Attached" when
              // the card merged into an existing patient via external_id
              // dedup, "Locked" when it created a fresh patient. Same colour
              // bucket; different verb. See cursor_logs/036 §16.
              label={t(
                card.matchedPatient
                  ? "patients_v2.card_status_attached"
                  : "patients_v2.card_status_locked",
                card.matchedPatient ? "Attached" : "Locked",
              )}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 500,
                flexShrink: 0,
                bgcolor: alpha(theme.palette.success.light, 0.18),
                color: theme.palette.success.light,
                border: `1px solid ${alpha(theme.palette.success.light, 0.4)}`,
                "& .MuiChip-icon": { color: theme.palette.success.light },
              }}
            />
          </Tooltip>
        )}
        {isMidFlight && (
          <CircularProgress
            size={16}
            sx={{ color: theme.palette.secondary.main, flexShrink: 0 }}
          />
        )}
        {card.status === "failed" && (
          <ErrorOutlineIcon
            sx={{
              fontSize: 18,
              color: theme.palette.error.light,
              flexShrink: 0,
            }}
          />
        )}

        {/* Joan, 2026-05-27: the per-card action button (Create / Retry /
            Go-to-patient) moved OUT of the header and into the affordance
            row next to the upload icon. The header now only carries the
            #N chip + name input + status icons. */}

      </Stack>

      {/* ── BODY — lighter outlined glass. Houses lifecycle banners, file
          chips, the Add More Details panel, and the upload chip. ──────── */}
      <Box
        sx={{
          background: bodyGradient,
          backdropFilter: "blur(8px)",
          color: theme.custom?.contentText?.primary ?? theme.palette.text.primary,
        }}
      >
        {/* Lifecycle banner — per-patient notifications live here, in the
            body, near the upload square (Joan, 2026-05-25). */}
        {(isMidFlight || card.status === "failed") && (
          <Box sx={{ px: 3, pt: 1, pb: 0.5 }}>
            {isMidFlight && (
              <Alert
                icon={<CircularProgress size={14} />}
                severity="info"
                sx={{
                  py: 0.25,
                  fontSize: "0.8rem",
                  alignItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.custom?.contentText?.primary,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                }}
              >
                {t(
                  "patients_v2.card_status_creating",
                  "Creating {name}…",
                ).replace("{name}", cardLabel || `#${index + 1}`)}
              </Alert>
            )}
            {card.status === "failed" && (
              <Alert
                icon={<ErrorOutlineIcon fontSize="small" />}
                severity="error"
                sx={{ py: 0.25, fontSize: "0.8rem", alignItems: "center" }}
              >
                {card.errorMessage ||
                  t(
                    "patients_v2.card_status_failed",
                    "Creation failed. Edit fields and retry.",
                  )}
              </Alert>
            )}
          </Box>
        )}

        {/* Cross-patient external_id conflict banner (behaviour D). Lists each
            id that already belongs to a different patient; submit stays blocked
            until the offending chip is removed. */}
        {!isReadOnly && hasExternalIdConflict && (
          <Box sx={{ px: 3, pt: 1, pb: 0.5 }}>
            <Alert
              icon={<ErrorOutlineIcon fontSize="small" />}
              severity="error"
              sx={{ py: 0.25, fontSize: "0.8rem", alignItems: "center" }}
            >
              {Array.from(externalIdConflicts.entries()).map(([tok, owner]) => (
                <Box key={tok}>
                  {t(
                    "patients_v2.external_id_conflict",
                    "ID {id} already belongs to {name} (#{pid}). Remove it to attach.",
                  )
                    .replace("{id}", tok)
                    .replace("{name}", owner.name)
                    .replace("{pid}", String(owner.id))}
                </Box>
              ))}
            </Alert>
          </Box>
        )}

        {/* Same external_id on two cards (§30). Blocks submit until one is
            removed, so the form can't create/attach two patients to one id. */}
        {!isReadOnly && hasDuplicateExternalId && (
          <Box sx={{ px: 3, pt: 1, pb: 0.5 }}>
            <Alert
              icon={<ErrorOutlineIcon fontSize="small" />}
              severity="error"
              sx={{ py: 0.25, fontSize: "0.8rem", alignItems: "center" }}
            >
              {t(
                "patients_v2.external_id_duplicate_card",
                "External ID {id} is also on another patient in this form. Remove the duplicate.",
              ).replace("{id}", Array.from(cardDuplicateTokens).join(", "))}
            </Alert>
          </Box>
        )}

        {/* Cross-environment lock (§35). The picked patient lives in an
            environment that is not the form's creation env, so attaching here
            would spawn a duplicate. Block Create and offer to activate that
            env — which makes it the creation env for the whole session. */}
        {!isReadOnly && crossEnvLocked && card.crossEnvLock && (
          <Box sx={{ px: 3, pt: 1, pb: 0.5 }}>
            <Alert
              icon={<ErrorOutlineIcon fontSize="small" />}
              severity="warning"
              sx={{ py: 0.25, fontSize: "0.8rem", alignItems: "center" }}
              action={
                onActivateEnvironment ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleActivateCrossEnv}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {t(
                      "patients_v2.cross_env_activate",
                      "Activate {env}",
                    ).replace("{env}", card.crossEnvLock.envName)}
                  </Button>
                ) : undefined
              }
            >
              {t(
                "patients_v2.cross_env_locked",
                "This patient is in {env}. Activate it to update this patient. All patients created in this session will then be added to {env}.",
              ).replace(/\{env\}/g, card.crossEnvLock.envName)}
            </Alert>
          </Box>
        )}

        {/* File chips — onPaper variant. */}
        {fileCount > 0 && (
          <Box
            sx={{
              px: 3,
              pt: 1,
              pb: 0.5,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {hasManyFiles && !chipsExpanded ? (
              <Chip
                icon={<FileIcon sx={{ fontSize: 14 }} />}
                label={t(
                  "patients_v2.card_file_summary_collapsed",
                  "{count} files — show all",
                ).replace("{count}", String(fileCount))}
                size="small"
                variant="outlined"
                onClick={() => setChipsExpanded(true)}
                sx={{
                  maxWidth: "100%",
                  borderColor: alpha(theme.palette.secondary.main, 0.55),
                  color: theme.custom?.contentText?.primary,
                  "& .MuiChip-icon": { color: theme.palette.secondary.main },
                }}
              />
            ) : (
              <>
                {card.files.map((file, fi) => (
                  <Chip
                    key={`${file.name}-${fi}`}
                    icon={<FileIcon sx={{ fontSize: 14 }} />}
                    label={`${file.name} (${formatFileSize(file.size)})`}
                    onDelete={
                      inputsDisabled ? undefined : () => handleFileRemove(fi)
                    }
                    deleteIcon={<CloseIcon />}
                    size="small"
                    variant="outlined"
                    sx={{
                      maxWidth: "100%",
                      borderColor: alpha(theme.palette.secondary.main, 0.55),
                      color: theme.custom?.contentText?.primary,
                      "& .MuiChip-icon": {
                        color: theme.palette.secondary.main,
                      },
                      "& .MuiChip-deleteIcon": {
                        color: alpha(theme.custom?.contentText?.muted ?? theme.palette.text.secondary, 0.7),
                        "&:hover": {
                          color: theme.custom?.contentText?.primary,
                        },
                      },
                    }}
                  />
                ))}
                {hasManyFiles && (
                  <Chip
                    label={t(
                      "patients_v2.card_file_summary_collapse",
                      "collapse",
                    )}
                    size="small"
                    variant="outlined"
                    onClick={() => setChipsExpanded(false)}
                    sx={{
                      borderColor: alpha(theme.palette.secondary.main, 0.55),
                      color: theme.custom?.contentText?.primary,
                    }}
                  />
                )}
              </>
            )}
          </Box>
        )}

        {/* ── Affordance row ────────────────────────────────────────────
            Left: "+ Add more details ▾" — info-tinted text button.
            Right: large rounded square with the upload icon centered.
            (Joan, 2026-05-25.) The lifecycle banner sits above this row so
            per-patient notifications live in the same visual region as the
            upload square. */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 3, py: 1.25, gap: 1.5 }}
        >
          {!isReadOnly && (
            <Button
              size="small"
              onClick={() => setDetailsOpen((v) => !v)}
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              endIcon={
                detailsOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 14 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 14 }} />
                )
              }
              disabled={inputsDisabled}
              sx={{
                // V1 pill parity (Joan, 2026-05-27): mirrors the "+ New
                // Environment" chip in AddPatientForm.tsx — height 24,
                // fontSize 0.72rem, info-tint, primary hover ramp.
                textTransform: "none",
                fontSize: "0.72rem",
                fontWeight: 500,
                px: 1.25,
                py: 0,
                minHeight: 24,
                height: 24,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                borderRadius: "12px",
                border: "1px solid transparent",
                backdropFilter: "blur(8px)",
                transition: "all 0.25s ease",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.18),
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                },
              }}
            >
              {detailsOpen
                ? t("patients_v2.hide_more_details", "Hide more details")
                : t("patients_v2.show_more_details", "Add more details")}
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Rounded square upload target — info-tinted, icon centered.
              Bigger than a chip, clear drop-target affordance. Drop-
              anywhere on the card body remains the primary upload path;
              this square is the explicit click target. */}
          <Tooltip
            title={t(
              "patients_v2.upload_affordance_tooltip",
              "Click here to browse, or drop files anywhere on this card.",
            )}
            arrow
            enterDelay={400}
          >
            <span>
              <IconButton
                onClick={handleBrowseClick}
                disabled={inputsDisabled}
                aria-label={t(
                  "patients_v2.upload_affordance_desktop",
                  "Upload files or drop anywhere",
                )}
                sx={{
                  // Joan, 2026-05-26: SAME width as the Create button above
                  // so the two are centred in the same vertical column.
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                  backdropFilter: "blur(8px)",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.18),
                    color: theme.palette.primary.main,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": { opacity: 0.45 },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Per-card action: Create / Retry (idle/failed) → Go-to-patient
              (succeeded). Rounded SQUARE to mirror the upload icon next to
              it. Joan, 2026-05-27: moved here from the header row. */}
          {showInlineCreate &&
            onSubmitProp &&
            (card.status === "idle" || card.status === "failed") && (
              <Tooltip
                title={createButtonTooltip}
                arrow
                placement="top"
              >
                <span>
                  <IconButton
                    onClick={handleCreateClick}
                    // Onboarding tutorial anchor (first card only, to match the
                    // prefilled name on [data-tour='patient-name']). The
                    // "submit-create-patient" step spotlights this button and
                    // the user's real click triggers the create. (V1 anchored
                    // its single Create button the same way.)
                    data-tour={index === 0 ? "submit-create-patient" : undefined}
                    disabled={
                      disabled ||
                      hasExternalIdConflict ||
                      hasDuplicateExternalId ||
                      crossEnvLocked ||
                      externalIdDisablesCreate ||
                      (!card.name.trim() &&
                        !card.externalId.trim() &&
                        !externalIdDraft.trim())
                    }
                    aria-label={
                      card.status === "failed"
                        ? t("patients_v2.card_retry", "Retry")
                        : t("patients_v2.card_create_one", "Create patient")
                    }
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      background:
                        card.status === "failed"
                          ? `linear-gradient(180deg, ${alpha(
                              theme.palette.error.main,
                              0.95,
                            )} 0%, ${alpha(
                              theme.palette.error.main,
                              0.85,
                            )} 100%)`
                          : `linear-gradient(180deg, ${alpha(
                              theme.palette.success.main,
                              0.95,
                            )} 0%, ${alpha(
                              theme.palette.success.main,
                              0.85,
                            )} 100%)`,
                      color: theme.palette.common.white,
                      border: `1px solid ${alpha(
                        theme.palette.common.white,
                        0.25,
                      )}`,
                      boxShadow: theme.custom?.glassShadow,
                      backdropFilter: "blur(6px)",
                      transition: "all .25s ease",
                      "&:hover": {
                        background:
                          card.status === "failed"
                            ? `linear-gradient(180deg, ${alpha(
                                theme.palette.error.dark,
                                1,
                              )} 0%, ${alpha(
                                theme.palette.error.dark,
                                0.9,
                              )} 100%)`
                            : `linear-gradient(180deg, ${alpha(
                                theme.palette.success.dark,
                                1,
                              )} 0%, ${alpha(
                                theme.palette.success.dark,
                                0.9,
                              )} 100%)`,
                        transform: "translateY(-1px)",
                      },
                      "&.Mui-disabled": {
                        background: alpha(theme.palette.common.white, 0.08),
                        color: alpha(theme.palette.common.white, 0.35),
                        border: `1px solid ${alpha(
                          theme.palette.common.white,
                          0.12,
                        )}`,
                      },
                    }}
                  >
                    {externalIdDisablesCreate ? (
                      <CircularProgress size={16} sx={{ color: theme.palette.common.white }} />
                    ) : card.status === "failed" ? (
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <PersonAddIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}

          {showInlineCreate &&
            card.status === "succeeded" &&
            card.createdPatientId && (
              <Tooltip
                title={t("patients_v2.card_go_to_patient", "Go to patient")}
                arrow
                placement="top"
              >
                <IconButton
                  component="a"
                  href={`/patients/${card.createdPatientId}/details`}
                  onClick={(e: React.MouseEvent) =>
                    goToPatient(e, card.createdPatientId as string)
                  }
                  aria-label={t(
                    "patients_v2.card_go_to_patient",
                    "Go to patient",
                  )}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: `linear-gradient(180deg, ${alpha(
                      theme.palette.primary.main,
                      0.95,
                    )} 0%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                    color: theme.palette.common.white,
                    border: `1px solid ${alpha(
                      theme.palette.common.white,
                      0.25,
                    )}`,
                    boxShadow: theme.custom?.glassShadow,
                    backdropFilter: "blur(6px)",
                    transition: "all .25s ease",
                    "&:hover": {
                      background: `linear-gradient(180deg, ${alpha(
                        theme.palette.primary.dark,
                        1,
                      )} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.tiff,.tif,.svs,.ndpi,.mrxs,.bif,.scn"
          />
        </Stack>

        {/* Add more details panel — collapsed by default, slides in below
            the affordance row when the user clicks the button. */}
        {detailsOpen && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <AddMoreDetailsPanel
              open={detailsOpen}
              description={card.description}
              onChangeDescription={(v) => onUpdate({ description: v })}
              matchedPatientName={card.matchedPatient?.name ?? null}
              disabled={inputsDisabled}
            />
          </Box>
        )}

        {/* Mobile-only Create button row */}
        {mobileCreateButton && (
          <Box sx={{ px: 3, pb: 1.5 }}>{mobileCreateButton}</Box>
        )}

        {/* Succeeded inline caption (when no Patient ID came back) */}
        {card.status === "succeeded" && !card.createdPatientId && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.custom?.contentText?.muted }}
            >
              {t(
                "patients_v2.card_status_created_no_link",
                "Patient created.",
              )}
            </Typography>
          </Box>
        )}
      </Box>
      </Box>

      {/* §16 "Attach to <name>" confirmation modal retired in §33. The
          header "Updating <name>" indicator + "Linked to" pill + the Create
          button's disabled-while-checking gate already convey that an attach
          is about to happen, so the modal duplicated info already on screen.
          Create now routes straight to onSubmit() in both create and attach
          modes; the backend decides via the resolved external_id. */}
    </Box>
  );
};

export default React.memo(PatientCard);
